import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Info } from "components/icons/Info";
import { TickSlim } from "components/icons/TickSlim";
import {
  DOCUMENT_TYPE_STATUS,
  RequiredItem,
} from "v2/store/entityDocumentsApi";
import { groupRequiredBySection } from "./groupRequiredBySection";
import { RequiredRowActions } from "./RequiredRowActions";
import { ScrollListHint } from "./ScrollListHint";
import { useScrollListHint } from "./useScrollListHint";

type RequiredDocumentListProps = {
  entityId: string;
  items: RequiredItem[];
  matchedIds: Set<string>;
  isBusy: boolean;

  listClassName?: string;
  hintClassName?: string;
};

type RequiredRowProps = {
  entityId: string;
  item: RequiredItem;
  isMatched: boolean;
  isBusy: boolean;
};

const MARKED_LABEL: Record<number, string> = {
  [DOCUMENT_TYPE_STATUS.UNAVAILABLE]: "Marked 'Unavailable'",
  [DOCUMENT_TYPE_STATUS.ALREADY_UPLOADED]: "Marked 'Already uploaded'",
};

const RequiredRow = ({
  entityId,
  item,
  isMatched,
  isBusy,
}: RequiredRowProps) => {
  const marked = MARKED_LABEL[item.status ?? 0];
  const isDone = isMatched || Boolean(marked);

  return (
    <div className="t-group t-flex t-min-h-7 t-items-center t-justify-between t-gap-2 t-rounded-lg t-px-3 t-py-0.5 t-transition-colors hover:t-bg-neutral-0">
      <span className="t-flex t-min-w-0 t-items-center t-gap-2">
        <span className="t-truncate t-text-body t-text-text-60">
          {item.label}
        </span>
        {item.isUrgent && (
          <span className="t-rounded t-bg-purple-10 t-px-1.5 t-py-0.5 t-text-[10px] t-font-bold t-uppercase t-tracking-wide t-text-purple">
            Urgent
          </span>
        )}
        {item.description && (
          <ToolTip text={item.description}>
            <Button
              customType="ghost_icon"
              size="xsmall"
              aria-label="More information"
              className="t-text-neutral-40"
            >
              <Info />
            </Button>
          </ToolTip>
        )}
        {marked && (
          <span className="t-shrink-0 t-rounded t-bg-neutral-10 t-px-1.5 t-py-0.5 t-text-body-sm t-text-neutral-50">
            {marked}
          </span>
        )}
      </span>

      <span className="t-flex t-h-7 t-shrink-0 t-items-center">
        {isMatched && (
          <span className="t-text-dark_green">
            <TickSlim />
          </span>
        )}
        {!isDone && !isBusy && (
          <RequiredRowActions entityId={entityId} item={item} />
        )}
      </span>
    </div>
  );
};

export const RequiredDocumentList = ({
  entityId,
  items,
  matchedIds,
  isBusy,
  listClassName,
  hintClassName,
}: RequiredDocumentListProps) => {
  const groups = groupRequiredBySection(items);

  const {
    ref: listRef,
    showHint: showScrollHint,
    onScroll: updateScrollHint,
    scrollDown: handleScrollDown,
  } = useScrollListHint<HTMLDivElement>(items.length);

  return (
    <>
      <div
        ref={listRef}
        onScroll={updateScrollHint}
        className={classNames(
          "hide-scrollbar t-pb-3 t-pr-1.5",
          listClassName ?? "pending-list"
        )}
      >
        {groups.map((group) => (
          <div key={group.section} className="t-mb-3 last:t-mb-0">
            <div className="t-mb-0.5 t-text-body-sm t-text-neutral-40">
              {group.section}
            </div>
            <ul className="t-my-0 t-flex t-list-none t-flex-col t-gap-0 t-pl-0">
              <AnimatePresence initial={false}>
                {group.items.map((item) => (
                  <motion.li
                    key={item.id}
                    layout
                    exit={{ x: 64, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                  >
                    <RequiredRow
                      entityId={entityId}
                      item={item}
                      isMatched={matchedIds.has(item.id)}
                      isBusy={isBusy}
                    />
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showScrollHint && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <ScrollListHint
              className={hintClassName ?? "pending-hint"}
              onClick={handleScrollDown}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
